import { resolutionListT, QualityType, qualityBaseWidths } from "../utils/constants";
import { resolutionList } from "../utils/constants";

export interface ScreenDimensions {
    width: number;
    height: number;
}

/**
 * Service for calculating screen dimensions based on quality and aspect ratio
 */
export class ScreenDimensionsService {
    // Quality to base width mapping
    private qualityBaseWidths: Record<QualityType, number> = qualityBaseWidths;

    /**
     * Get the width for a specific quality
     */
    public getBaseWidthForQuality(quality: QualityType): number {
        return this.qualityBaseWidths[quality] || this.qualityBaseWidths["Full HD"];
    }

    /**
     * Add or update a quality preset
     */
    public setQualityBaseWidth(quality: QualityType, width: number): void {
        this.qualityBaseWidths[quality] = width;
    }

    /* -------------  ASPECT-RATIO UTILS  ------------- */
    /** "16x9" -> 16 / 9  (or false, if null) */
    public ratioFromAspect(aspect: resolutionListT | null): number | false {
        if (!aspect || !aspect.label) return false;
        const [w, h] = this.parseLabel(aspect.label);
        if (h === 0) return false;
        return w / h;
    }

    /** Returns [w, h] from "w×h" */
    public parseLabel(label: string): [number, number] {
        return label.split("x").map(Number) as [number, number];
    }

    // Private helper for height calculation
    private _calculateHeight(width: number, widthRatio: number, heightRatio: number): number {
        if (widthRatio === 0) return 0;
        return Math.round(width * (heightRatio / widthRatio));
    }

    // Private helper for width calculation
    private _calculateWidth(height: number, widthRatio: number, heightRatio: number): number {
        if (heightRatio === 0) return 0;
        return Math.round(height * (widthRatio / heightRatio));
    }

    /**
     * Calculate dimensions from aspect ratio and quality
     */
    public calculateFromAspectRatio(
        aspectRatio: resolutionListT | null,
        quality: QualityType
    ): ScreenDimensions {
        if (!aspectRatio) {
            return { width: 0, height: 0 };
        }

        const [widthRatio, heightRatio] = this.parseLabel(aspectRatio.label);
        const base = this.getBaseWidthForQuality(quality);
        let width: number, height: number;
        if (widthRatio >= heightRatio) {
            // Landscape or square: use base as width
            width = base;
            height = this.calculateHeightFromWidth(width, aspectRatio);
        } else {
            // Portrait: use base as height
            height = base;
            width = this.calculateWidthFromHeight(height, aspectRatio);
        }
        return { width, height };
    }

    /**
     * Calculate height from width and aspect ratio
     */
    public calculateHeightFromWidth(
        width: number,
        aspectRatio: resolutionListT | null
    ): number {
        if (!aspectRatio) {
            return 0;
        }

        const [widthRatio, heightRatio] = this.parseLabel(aspectRatio.label);
        return this._calculateHeight(width, widthRatio, heightRatio);
    }

    /**
     * Calculate width from height and aspect ratio
     */
    public calculateWidthFromHeight(
        height: number,
        aspectRatio: resolutionListT | null
    ): number {
        if (!aspectRatio) {
            return 0;
        }

        const [widthRatio, heightRatio] = this.parseLabel(aspectRatio.label);
        return this._calculateWidth(height, widthRatio, heightRatio);
    }

    /**
     * Scale dimensions by a factor
     */
    public scaleDimensions(
        dimensions: ScreenDimensions,
        scaleFactor: number
    ): ScreenDimensions {
        return {
            width: Math.round(dimensions.width * scaleFactor),
            height: Math.round(dimensions.height * scaleFactor),
        };
    }

    /**
      * If the frame extends beyond the wrapper, return the "cropped" frameSize and position.
      * Call after any size/position change
      */
    public clampToWrapper(
        frame: ScreenDimensions,
        pos: { x: number; y: number },
        wrap: { w: number; h: number },
        aspect: resolutionListT | null
    ): { frame: ScreenDimensions; pos: { x: number; y: number } } {
        let { width, height } = frame;
        let { x, y } = pos;

        if (width > wrap.w) {
            width = wrap.w;
            height = this.calculateHeightFromWidth(width, aspect) || height;
        }
        if (height > wrap.h) {
            height = wrap.h;
            width = this.calculateWidthFromHeight(height, aspect) || width;
        }

        x = Math.max(0, Math.min(x, wrap.w - width));
        y = Math.max(0, Math.min(y, wrap.h - height));

        return { frame: { width, height }, pos: { x, y } };
    }

    /**
     * Find the closest matching aspect ratio from supported list based on dimensions
     */
    public findClosestAspectRatio(width: number, height: number): resolutionListT {
        if (height === 0) return resolutionList[0];
        const currentRatio = width / height;

        return resolutionList.reduce((best, res) => {
            const resRatio = this.ratioFromAspect(res);
            const bestRatio = this.ratioFromAspect(best);

            if (resRatio === false || bestRatio === false) {
                return best;
            }

            return Math.abs(resRatio - currentRatio) < Math.abs(bestRatio - currentRatio)
                ? res
                : best;
        }, resolutionList[0]);
    }

    /**
     * Fit a frame into the wrapper, preserving aspect ratio and centering it.
     */
    public fitFrameToWrapper(
        wrap: { w: number; h: number },
        aspect: resolutionListT | null,
        margin: number = 0
    ): { frame: ScreenDimensions; pos: { x: number; y: number } } {
        if (!aspect) {
            return { frame: { width: 0, height: 0 }, pos: { x: 0, y: 0 } };
        }
        const [wR, hR] = this.parseLabel(aspect.label);

        const availW = Math.round(wrap.w - margin * 2);
        const availH = Math.round(wrap.h - margin * 2);

        let width = availW;
        let height = Math.round((width * hR) / wR);

        if (height > availH) {
            height = availH;
            width = Math.round((height * wR) / hR);
        }

        const x = Math.round(Math.max(0, (availW - width) / 2)) + margin;
        const y = Math.round(Math.max(0, (availH - height) / 2)) + margin;
        return { frame: { width, height }, pos: { x, y } };
    }

    /**
     * Calculates the new frame state (position and dimensions) during a resize operation,
     * preserving the center and clamping within the wrapper and margins.
     */
    public calculateResizedFrame(
        currentFrame: { x: number; y: number; width: number; height: number },
        newSize: { width: number; height: number },
        wrapperSize: { w: number; h: number },
        margin: number
    ): { x: number; y: number; width: number; height: number } {
        const { width: newW, height: newH } = newSize;
        const oldCenterX = currentFrame.x + currentFrame.width / 2;
        const oldCenterY = currentFrame.y + currentFrame.height / 2;

        // Ensure dimensions do not exceed wrapper considering margins
        const clampedW = Math.min(newW, wrapperSize.w - 2 * margin);
        const clampedH = Math.min(newH, wrapperSize.h - 2 * margin);

        // Calculate new centered position based on clamped dimensions
        let finalX = oldCenterX - clampedW / 2;
        let finalY = oldCenterY - clampedH / 2;

        // Clamp position within margins
        finalX = Math.max(margin, Math.min(finalX, wrapperSize.w - clampedW - margin));
        finalY = Math.max(margin, Math.min(finalY, wrapperSize.h - clampedH - margin));

        return { x: finalX, y: finalY, width: clampedW, height: clampedH };
    }

    /**
     * Calculate adjusted FOV based on frame width relative to reference width.
     * Uses perspective projection formula to maintain scene consistency.
     */
    public calculateAdjustedFOV(
        innerFOVDegrees: number,
        referenceWidth: number,
        frameWidth: number,
        maxFOV: number
    ): number {
        // Convert to radians for calculation
        const innerFOVRadians = (innerFOVDegrees * Math.PI) / 180;
        // Calculate actual FOV using perspective projection formula
        const actualFOVRadians = 2 * Math.atan(
            Math.tan(innerFOVRadians / 2) * (referenceWidth / frameWidth)
        );


        // Convert back to degrees and clamp to maxFOV
        const actualFOVDegrees = Math.min(
            (actualFOVRadians * 180) / Math.PI,
            maxFOV
        );
        return actualFOVDegrees;
    }

    /**
     * Scale a manually sized frame proportionally with wrapper changes,
     * preserving user aspect ratio and margins, then center it.
     * @param manual - the user-defined frame dimensions
     * @param wrap - current wrapper width/height
     * @param manualWidthPct - width percentage of wrapper at manual resize
     * @param margin - margin in pixels to respect on each side
     * @returns new frame dimensions and position
     */
    public scaleFrameToWrapper(
        manual: { width: number; height: number },
        wrap: { w: number; h: number },
        manualWidthPct: number,
        margin: number
    ): { frame: ScreenDimensions; pos: { x: number; y: number } } {
        const { width: manualW, height: manualH } = manual;
        if (manualW <= 0 || manualH <= 0) {
            return { frame: { width: 0, height: 0 }, pos: { x: margin, y: margin } };
        }
        const aspect = manualW / manualH;
        const innerW = wrap.w - margin * 2;
        const innerH = wrap.h - margin * 2;
        // desired width based on saved percentage
        const desiredW = wrap.w * manualWidthPct;
        // clamp width to inner bounds
        const width = Math.min(desiredW, innerW);
        // derive height from aspect ratio
        let height = width / aspect;
        // if that height overflows, clamp to inner height
        if (height > innerH) {
            height = innerH;
        }
        // recalc width to maintain aspect after height clamp
        const finalW = height * aspect;
        const finalH = height;
        // center within wrapper respecting margin
        const x = Math.max(margin, Math.min((wrap.w - finalW) / 2, wrap.w - finalW - margin));
        const y = Math.max(margin, Math.min((wrap.h - finalH) / 2, wrap.h - finalH - margin));
        return { frame: { width: finalW, height: finalH }, pos: { x, y } };
    }
} 